import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'

/**
 * Flexible Pagination (server-driven)
 * - Hỗ trợ 2 kiểu meta từ backend:
 *   A) Page-based: { page, page_size, total, total_pages? }
 *   B) Offset-based: { skip, limit, total }
 *
 * Props:
 * - meta: object  (A hoặc B, hoặc có cả total_pages/has_next/has_prev nếu BE trả về)
 * - onChange: ({ page, page_size }) => void   // gọi khi đổi trang hoặc page size
 * - pageSizeOptions: number[]                  // mặc định [10, 25, 50, 100]
 * - className: string                          // optional
 * - compact: boolean                           // gọn hơn trên mobile
 *
 * Gợi ý backend:
 * - Page-based request:   GET ?page=1&page_size=25
 * - Offset-based request: GET ?skip=0&limit=25  (page = skip/limit + 1)
 */
function normalize(meta) {
    // Page-based
    if (meta && (meta.page || meta.page === 0) && meta.page_size) {
        const page = Number(meta.page) || 1
        const page_size = Number(meta.page_size) || 10
        const total = Number(meta.total ?? 0)
        const total_pages =
            Number(meta.total_pages) ||
            (total > 0 ? Math.max(1, Math.ceil(total / page_size)) : 1)
        const has_prev = meta.has_prev ?? page > 1
        const has_next = meta.has_next ?? page < total_pages
        const start = total ? (page - 1) * page_size + 1 : 0
        const end = total ? Math.min(total, page * page_size) : 0
        return {
            mode: 'page',
            page,
            page_size,
            total,
            total_pages,
            has_prev,
            has_next,
            start,
            end,
        }
    }

    // Offset-based
    if (meta && (meta.skip || meta.skip === 0) && meta.limit) {
        const skip = Number(meta.skip) || 0
        const limit = Number(meta.limit) || 10
        const total = Number(meta.total ?? 0)
        const page = Math.floor(skip / limit) + 1
        const page_size = limit
        const total_pages =
            total > 0 ? Math.max(1, Math.ceil(total / limit)) : 1
        const has_prev = page > 1
        const has_next = page < total_pages
        const start = total ? skip + 1 : 0
        const end = total ? Math.min(total, skip + limit) : 0
        return {
            mode: 'offset',
            page,
            page_size,
            total,
            total_pages,
            has_prev,
            has_next,
            start,
            end,
        }
    }

    // Fallback
    const page = Number(meta?.page || 1)
    const page_size = Number(meta?.page_size || meta?.limit || 10)
    const total = Number(meta?.total ?? 0)
    const total_pages =
        Number(meta?.total_pages) ||
        (total > 0 ? Math.max(1, Math.ceil(total / page_size)) : 1)
    const has_prev = page > 1
    const has_next = page < total_pages
    const start = total ? (page - 1) * page_size + 1 : 0
    const end = total ? Math.min(total, page * page_size) : 0
    return {
        mode: 'page',
        page,
        page_size,
        total,
        total_pages,
        has_prev,
        has_next,
        start,
        end,
    }
}

function getPages(current, total, maxVisible = 7) {
    if (total <= maxVisible) {
        return Array.from({ length: total }, (_, i) => i + 1)
    }
    const pages = []
    const left = Math.max(1, current - 2)
    const right = Math.min(total, current + 2)

    // Always include first / last with ellipsis when needed
    if (left > 2) {
        pages.push(1, '…')
    } else {
        for (let i = 1; i < left; i++) pages.push(i)
    }

    for (let i = left; i <= right; i++) pages.push(i)

    if (right < total - 1) {
        pages.push('…', total)
    } else {
        for (let i = right + 1; i <= total; i++) pages.push(i)
    }

    return pages
}

const Pagination = ({
    meta,
    onChange,
    pageSizeOptions = [10, 25, 50, 100],
    className = '',
    compact = false,
}) => {
    const norm = useMemo(() => normalize(meta), [meta])
    const [jump, setJump] = useState('')

    const pages = useMemo(
        () => getPages(norm.page, norm.total_pages, compact ? 5 : 7),
        [norm.page, norm.total_pages, compact]
    )

    const goto = (page) => {
        const p = Math.max(1, Math.min(norm.total_pages, Number(page) || 1))
        onChange({ page: p, page_size: norm.page_size })
    }

    const changeSize = (size) => {
        const s = Number(size) || norm.page_size
        // Reset về page 1 khi đổi page size để tránh out-of-range
        onChange({ page: 1, page_size: s })
    }

    return (
        <div
            className={
                'w-full flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between ' +
                className
            }
        >
            {/* Summary */}
            <div className="text-xs sm:text-sm text-slate-400">
                {norm.total ? (
                    <>
                        Showing{' '}
                        <span className="text-slate-200">
                            {norm.start}–{norm.end}
                        </span>{' '}
                        of <span className="text-slate-200">{norm.total}</span>
                    </>
                ) : (
                    <>—</>
                )}
            </div>

            {/* Controls */}
            <div className="flex flex-wrap items-center gap-2">
                {/* Page size */}
                <label className="flex items-center gap-2 text-xs sm:text-sm text-slate-400">
                    Rows
                    <select
                        value={norm.page_size}
                        onChange={(e) => changeSize(e.target.value)}
                        className="h-9 rounded-lg bg-slate-900 border border-slate-700 text-slate-200 px-2 focus:outline-none focus:ring-2 focus:ring-indigo-500/60"
                    >
                        {pageSizeOptions.map((n) => (
                            <option key={n} value={n}>
                                {n}
                            </option>
                        ))}
                    </select>
                    /page
                </label>

                {/* Pager */}
                <div className="isolate inline-flex overflow-hidden rounded-lg border border-slate-700 bg-slate-900">
                    <button
                        onClick={() => goto(1)}
                        disabled={!norm.has_prev}
                        className="px-3 h-9 text-slate-300 hover:bg-white/10 disabled:opacity-40"
                        aria-label="First page"
                        title="First"
                    >
                        «
                    </button>
                    <button
                        onClick={() => goto(norm.page - 1)}
                        disabled={!norm.has_prev}
                        className="px-3 h-9 text-slate-300 hover:bg-white/10 disabled:opacity-40"
                        aria-label="Previous page"
                        title="Previous"
                    >
                        ‹
                    </button>

                    {pages.map((p, i) =>
                        p === '…' ? (
                            <span
                                key={`e-${i}`}
                                className="px-3 h-9 grid place-items-center text-slate-400 select-none"
                            >
                                …
                            </span>
                        ) : (
                            <button
                                key={p}
                                onClick={() => goto(p)}
                                className={
                                    'px-3 h-9 text-sm font-semibold ' +
                                    (p === norm.page
                                        ? 'bg-indigo-600 text-white'
                                        : 'text-slate-200 hover:bg-white/10')
                                }
                                aria-current={
                                    p === norm.page ? 'page' : undefined
                                }
                            >
                                {p}
                            </button>
                        )
                    )}

                    <button
                        onClick={() => goto(norm.page + 1)}
                        disabled={!norm.has_next}
                        className="px-3 h-9 text-slate-300 hover:bg-white/10 disabled:opacity-40"
                        aria-label="Next page"
                        title="Next"
                    >
                        ›
                    </button>
                    <button
                        onClick={() => goto(norm.total_pages)}
                        disabled={!norm.has_next}
                        className="px-3 h-9 text-slate-300 hover:bg-white/10 disabled:opacity-40"
                        aria-label="Last page"
                        title="Last"
                    >
                        »
                    </button>
                </div>

                {/* Go to page (optional) */}
                <form
                    onSubmit={(e) => {
                        e.preventDefault()
                        if (jump) goto(jump)
                        setJump('')
                    }}
                    className="flex items-center gap-2"
                >
                    <input
                        value={jump}
                        onChange={(e) => setJump(e.target.value)}
                        placeholder="Page Number"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        className="h-9 w-28 text-sm rounded-lg bg-slate-900 border border-slate-700 text-slate-200 px-2 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/60"
                    />
                    <button
                        type="submit"
                        className="h-9 px-3 rounded-lg border border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700/80"
                    >
                        Go
                    </button>
                </form>
            </div>
        </div>
    )
}

Pagination.propTypes = {
    meta: PropTypes.object.isRequired, // see normalize() for accepted shapes
    onChange: PropTypes.func.isRequired, // ({ page, page_size }) => void
    pageSizeOptions: PropTypes.arrayOf(PropTypes.number),
    className: PropTypes.string,
    compact: PropTypes.bool,
}

export default Pagination
